// Monarch SPA API + auth client. Extracted from app.jsx (decomposition).
// Self-contained: only browser APIs (fetch, localStorage, window) — no
// component/state deps — so every component can import `api` from here.

// `/tasks` was the legacy pis-vm base path; empty on Cloud Run. Guarded so
// the module is importable under Node (tests) where `window` is undefined.
export const API_BASE =
  (typeof window !== 'undefined' && window.location.pathname.startsWith('/tasks')) ? '/tasks' : '';

// Firebase ID tokens expire after 1 hour. If a request 401s and we have
// a refresh token, swap it for a fresh ID token and retry once before
// surfacing the error. Without this, long testing sessions or laptops
// left open would surface raw `Authentication required` JSON the moment
// the cached token aged out. Returns the new ID token or null.
let _refreshInFlight = null;
async function refreshIdToken() {
  if (_refreshInFlight) return _refreshInFlight;
  const refreshToken = localStorage.getItem('monarchRefreshToken');
  const apiKey = localStorage.getItem('monarchFirebaseApiKey');
  if (!refreshToken || !apiKey) return null;
  _refreshInFlight = (async () => {
    try {
      const res = await fetch(`https://securetoken.googleapis.com/v1/token?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(refreshToken)}`,
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data?.id_token) {
        localStorage.setItem('monarchIdToken', data.id_token);
        if (data.refresh_token) localStorage.setItem('monarchRefreshToken', data.refresh_token);
        return data.id_token;
      }
      return null;
    } catch (_) { return null; }
    finally { setTimeout(() => { _refreshInFlight = null; }, 0); }
  })();
  return _refreshInFlight;
}

// Phase C1: route every call through /api/v1/* canonically. The server's
// top-of-stack shim still accepts /api/* for any client that hasn't picked
// up this build, so the swap is safe. Call sites stay unchanged; toV1()
// handles the prefix swap.
export function toV1(path) {
  if (typeof path !== 'string') return path;
  if (path.startsWith('/api/v1/') || path === '/api/v1') return path;
  if (path.startsWith('/api/')) return '/api/v1/' + path.slice('/api/'.length);
  if (path === '/api') return '/api/v1';
  return path;
}

// Attach the Firebase ID token (set by exchangeFirebaseCustomToken after
// legacy login) via Authorization: Bearer. The server prefers Bearer and
// falls back to the x-user-id header during the migration, so existing call
// sites that still pass x-user-id keep working.
export async function api(method, path, body, extraHeaders) {
  const headers = { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(extraHeaders || {}) };
  const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('monarchIdToken') : null;
  if (token && !headers['Authorization']) headers['Authorization'] = `Bearer ${token}`;
  // Tag every request with the build version so the server's auth-mix
  // telemetry can verify the field is on a build ≥ the v1 cutover.
  const build = (typeof window !== 'undefined' && window.__MONARCH_BUILD) || '';
  if (build && !headers['x-monarch-build']) headers['x-monarch-build'] = build;
  const url = API_BASE + toV1(path);
  let res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  // 401 once → try refreshing the Firebase ID token and retry. Stops
  // long-running tabs from surfacing raw `Authentication required` JSON
  // when the 1-hour ID token aged out mid-session (W-107 / W-109).
  if (res.status === 401 && token) {
    const fresh = await refreshIdToken();
    if (fresh) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${fresh}` };
      res = await fetch(url, {
        method,
        headers: retryHeaders,
        body: body ? JSON.stringify(body) : undefined,
      });
    }
  }
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Exchange a Firebase custom token for an ID token via the Identity Toolkit
// REST API. Stores the ID token in localStorage so api() picks it up on
// every subsequent request. Returns true on success, false on failure
// (caller treats failure as "stay on legacy x-user-id auth"; the user is
// still logged in via the existing session, just not yet migrated).
export async function exchangeFirebaseCustomToken(customToken, apiKey) {
  if (!customToken || !apiKey) return false;
  try {
    const url = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken?key=${apiKey}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: customToken, returnSecureToken: true }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data?.idToken) {
      localStorage.setItem('monarchIdToken', data.idToken);
      if (data.refreshToken) localStorage.setItem('monarchRefreshToken', data.refreshToken);
      // Stash the API key so refreshIdToken() can mint fresh tokens
      // when the 1-hour ID token expires mid-session.
      localStorage.setItem('monarchFirebaseApiKey', apiKey);
      return true;
    }
    return false;
  } catch (_) { return false; }
}

export function clearFirebaseSession() {
  try {
    localStorage.removeItem('monarchIdToken');
    localStorage.removeItem('monarchRefreshToken');
    localStorage.removeItem('monarchFirebaseApiKey');
  } catch (_) {}
}
